// Collection hero component for collection pages.

import React from 'react';
import { ASSETS } from '@airhead-games/shared/configs';
import type { Collection } from '@airhead-games/shared/content';
import { cn } from '@/utilities/cn';
import BackgroundBlurred from '@/content/rendering/chrome/BackgroundBlurred';
import BackgroundGradient from '@/content/rendering/chrome/BackgroundGradient';
import { getCollectionHeroIcon } from '@/content/rendering/chrome/heroIcons';
import RichTextRenderer from '@/content/rendering/renderers/RichTextRenderer';

interface CollectionHeroProps {
  collection: Collection;
}

const CollectionHero: React.FC<CollectionHeroProps> = ({ collection }) => {
  const showHeroDescription = Boolean(collection.hero?.showDescription);
  const heroImageAsset = collection.hero?.imageAsset;
  const useBlurredImageBackground = Boolean(
    collection.hero?.background === 'blurred-image' && heroImageAsset
  );
  const useGradientCardBackground =
    collection.hero?.background === 'gradient-card';
  const usePaddedHero = useBlurredImageBackground || useGradientCardBackground;
  const useLightHeroText = useGradientCardBackground;
  const HeroIcon = useGradientCardBackground
    ? getCollectionHeroIcon(collection)
    : undefined;
  const showHeroImage = Boolean(heroImageAsset && !HeroIcon);

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-lg',
        useGradientCardBackground && 'ring-1 ring-white/20'
      )}
    >
      {useGradientCardBackground && (
        <BackgroundGradient
          family={collection.hero?.backgroundAccentFamily ?? 'lime'}
        />
      )}
      {useBlurredImageBackground && heroImageAsset && (
        <BackgroundBlurred imageAsset={heroImageAsset} />
      )}
      {HeroIcon && (
        <HeroIcon
          aria-hidden="true"
          strokeWidth={1.75}
          className="pointer-events-none absolute top-5 right-5 z-10 size-9 text-white sm:top-6 sm:right-6 sm:size-10 md:top-8 md:right-8 md:size-12 lg:top-10 lg:right-10 lg:size-14"
        />
      )}
      <div
        className={cn(
          'relative grid gap-6 md:gap-8',
          showHeroImage &&
            'xl:grid-cols-[minmax(0,1fr)_minmax(18rem,26rem)] xl:items-center 2xl:grid-cols-[minmax(0,1fr)_minmax(22rem,32rem)]'
        )}
      >
        <div
          className={cn(
            usePaddedHero
              ? 'flex max-w-5xl flex-col gap-4 p-5 sm:p-6 md:p-8 lg:p-10'
              : 'flex max-w-5xl flex-col gap-4',
            HeroIcon && 'pr-16 sm:pr-20 md:pr-24 lg:pr-28'
          )}
        >
          <h1
            className={
              useLightHeroText
                ? 'font-heading text-4xl leading-tight font-semibold tracking-tight text-white sm:text-5xl lg:text-6xl xl:text-6xl 2xl:text-7xl'
                : 'font-heading text-4xl leading-tight font-semibold tracking-tight sm:text-5xl lg:text-6xl xl:text-6xl 2xl:text-7xl'
            }
          >
            <RichTextRenderer
              nodes={
                collection.hero?.title ?? [
                  { type: 'text', text: collection.title },
                ]
              }
            />
          </h1>
          {showHeroDescription &&
            (collection.hero?.description || collection.description) && (
              <p
                className={
                  useLightHeroText
                    ? 'max-w-4xl text-base leading-6 text-white/80 sm:text-lg sm:leading-7 xl:text-xl xl:leading-8'
                    : 'max-w-4xl text-base leading-6 text-muted-foreground sm:text-lg sm:leading-7 xl:text-xl xl:leading-8'
                }
              >
                {collection.hero?.description ? (
                  <RichTextRenderer nodes={collection.hero.description} />
                ) : (
                  collection.description
                )}
              </p>
            )}
        </div>
        {showHeroImage && heroImageAsset && (
          <div
            className={
              usePaddedHero
                ? 'p-5 pt-0 sm:p-6 sm:pt-0 md:p-8 md:pt-0 xl:p-6 2xl:p-8'
                : undefined
            }
          >
            <img
              src={ASSETS[heroImageAsset]}
              alt={collection.hero?.imageAlt ?? collection.title}
              className="aspect-[4/3] w-full rounded-lg border border-border bg-muted object-cover"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CollectionHero;
